import React from "react";

import { AutoFormFieldProps } from "@autoform/react";

import { EnrichmentStatusBadge } from "@/components/common/enrichment-status-badge";
import { enrichmentStatusLabel } from "@/lib/enrichment";

/**
 * Read-only display for an entity's `enrichmentStatus` inside an AutoForm.
 * Register globally as the `enrichmentStatus` fieldType.
 *
 * Why a component rather than a labelled select: `@autoform/zod` derives a
 * select's options from `_def.values` and, for a `z.enum`, hard-codes each one
 * to `[value, value]` — `FieldConfig` has no `options` escape hatch, and
 * `z.nativeEnum` is worse (it validates on the object's *values*, so a
 * label-valued map rejects the wire value the form was loaded with). There is
 * no way to hand AutoForm a label map for an enum, so the label has to come
 * from a field component. This is why `/events/edit/:id` showed a disabled
 * "Enrichment Status" reading `NeedsReview` beside an EventSummary aside
 * reading "Needs Review".
 *
 * Both call sites were already `inputProps: { disabled: true }` — no curator
 * has ever been able to change this value — so dropping the select chrome also
 * stops the form offering an affordance it never honoured. The value stays in
 * form state untouched, so dirty-tracking and submit are unaffected.
 */
export const EnrichmentStatusField: React.FC<AutoFormFieldProps> = ({
  value,
}) => {
  const status = typeof value === "string" ? value : null;
  return (
    <div className="flex h-9 items-center">
      {status ? (
        <EnrichmentStatusBadge status={status} />
      ) : (
        <span className="text-sm text-muted-foreground">
          {enrichmentStatusLabel(null)}
        </span>
      )}
    </div>
  );
};

export default EnrichmentStatusField;
